import { useEffect, useRef } from 'react'

/**
 * OceanAtmosphere — sfondo marino vivo, generato via Canvas (nessun asset da scaricare).
 *
 * Perché così: l'atmosfera sensoriale (pilastro "Blue Mind" del piano di coinvolgimento)
 * deve dare la calma del mare senza megabyte di video né dipendenze esterne. Disegniamo
 * due strati leggeri:
 *   1) raggi di luce che filtrano dall'alto (caustiche), che ondeggiano piano;
 *   2) particelle di plancton/bollicine che salgono lentamente verso la superficie.
 *
 * Rispetta `prefers-reduced-motion`: in quel caso disegna UN fotogramma statico e si ferma
 * (niente animazione), così chi soffre di motion sickness non subisce movimento.
 *
 * Va posizionato in un contenitore `relative`, dietro al contenuto (z-0). Non intercetta
 * il puntatore. Si mette in pausa quando la tab non è visibile (risparmio batteria/CPU).
 */

type Particle = {
  x: number
  y: number
  r: number
  speed: number
  drift: number
  phase: number
  alpha: number
}

interface OceanAtmosphereProps {
  /** Densità particelle: più basso = più fitto. Default 14000 px² per particella. */
  density?: number
  /** Colore base delle particelle (luce sospesa). */
  particleColor?: string
  /** Intensità dei raggi di luce (0–1). Default 0.5. */
  rayStrength?: number
  className?: string
}

export default function OceanAtmosphere({
  density = 14000,
  particleColor = '255, 255, 255',
  rayStrength = 0.5,
  className = '',
}: OceanAtmosphereProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let width = 0
    let height = 0
    let particles: Particle[] = []
    let rays: { x: number; w: number; speed: number; phase: number; alpha: number }[] = []
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    const rand = (a: number, b: number) => a + Math.random() * (b - a)

    function seed() {
      const count = Math.max(18, Math.floor((width * height) / density))
      particles = Array.from({ length: count }, () => ({
        x: rand(0, width),
        y: rand(0, height),
        r: rand(0.6, 2.4),
        speed: rand(4, 16), // px/s verso l'alto
        drift: rand(-6, 6), // ondeggiamento orizzontale
        phase: rand(0, Math.PI * 2),
        alpha: rand(0.06, 0.35),
      }))
      const rayCount = Math.max(3, Math.round(width / 320))
      rays = Array.from({ length: rayCount }, (_, i) => ({
        x: (width / rayCount) * i + rand(-40, 40),
        w: rand(70, 180),
        speed: rand(0.02, 0.06),
        phase: rand(0, Math.PI * 2),
        alpha: rand(0.04, 0.1) * rayStrength * 2,
      }))
    }

    function resize() {
      const parent = canvas!.parentElement
      width = parent ? parent.clientWidth : window.innerWidth
      height = parent ? parent.clientHeight : window.innerHeight
      canvas!.width = Math.floor(width * dpr)
      canvas!.height = Math.floor(height * dpr)
      canvas!.style.width = `${width}px`
      canvas!.style.height = `${height}px`
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
      seed()
    }

    function drawRays(t: number) {
      for (const ray of rays) {
        const sway = Math.sin(t * ray.speed + ray.phase) * 40
        const x = ray.x + sway
        const grad = ctx!.createLinearGradient(x, 0, x + ray.w * 0.4, height)
        grad.addColorStop(0, `rgba(${particleColor}, ${ray.alpha})`)
        grad.addColorStop(1, `rgba(${particleColor}, 0)`)
        ctx!.fillStyle = grad
        ctx!.beginPath()
        ctx!.moveTo(x, 0)
        ctx!.lineTo(x + ray.w, 0)
        ctx!.lineTo(x + ray.w * 1.6 + sway, height)
        ctx!.lineTo(x + sway * 1.4, height)
        ctx!.closePath()
        ctx!.fill()
      }
    }

    function drawParticles(t: number, dt: number) {
      for (const p of particles) {
        if (dt > 0) {
          p.y -= p.speed * dt
          p.x += Math.sin(t * 0.5 + p.phase) * p.drift * dt
          if (p.y < -4) {
            p.y = height + rand(0, 40)
            p.x = rand(0, width)
          }
          if (p.x < -4) p.x = width + 4
          if (p.x > width + 4) p.x = -4
        }
        ctx!.beginPath()
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx!.fillStyle = `rgba(${particleColor}, ${p.alpha})`
        ctx!.fill()
      }
    }

    let last = 0
    function frame(now: number) {
      const t = now / 1000
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0
      last = now
      ctx!.clearRect(0, 0, width, height)
      drawRays(t)
      drawParticles(t, dt)
      rafRef.current = requestAnimationFrame(frame)
    }

    function staticFrame() {
      // reduced-motion: un solo fotogramma, fermo.
      ctx!.clearRect(0, 0, width, height)
      drawRays(0)
      drawParticles(0, 0)
    }

    function onVisibility() {
      if (document.hidden) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      } else if (!reduceMotion && rafRef.current === null) {
        last = 0
        rafRef.current = requestAnimationFrame(frame)
      }
    }

    resize()
    window.addEventListener('resize', resize)
    document.addEventListener('visibilitychange', onVisibility)

    if (reduceMotion) {
      staticFrame()
    } else {
      rafRef.current = requestAnimationFrame(frame)
    }

    return () => {
      window.removeEventListener('resize', resize)
      document.removeEventListener('visibilitychange', onVisibility)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [density, particleColor, rayStrength])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  )
}
